import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, updateDoc, doc, addDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

export default function Withdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'withdrawals'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setWithdrawals(data);
    });
    return () => unsub();
  }, []);

  const handleComplete = async (w) => {
    if (!window.confirm(`৳${w.amount} — ${w.number} এ টাকা পাঠানো হয়েছে confirm করো?`)) return;
    setLoading(w.id);
    try {
      // Update withdrawal status
      await updateDoc(doc(db, 'withdrawals', w.id), {
        status: 'completed',
        completedAt: serverTimestamp(),
      });

      // Update wallet history
      await addDoc(collection(db, 'walletHistory'), {
        userId: w.userId,
        type: 'withdraw_complete',
        amount: w.amount,
        note: `Withdraw completed via ${w.method} to ${w.number}`,
        balanceAfter: w.balanceAfter ?? 0,
        createdAt: serverTimestamp(),
      });

      alert('✅ Withdrawal completed!');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(null);
  };

  const handleReject = async (w) => {
    if (!window.confirm(`এই withdrawal reject করবে? টাকা user এর wallet এ ফেরত যাবে।`)) return;
    setLoading(w.id);
    try {
      // Refund balance
      const userRef = doc(db, 'users', w.userId);
      const userSnap = await (await import('firebase/firestore')).getDoc(userRef);
      const currentBalance = userSnap.data()?.balance ?? 0;
      const newBalance = currentBalance + w.amount;

      await updateDoc(userRef, { balance: newBalance });

      // Update withdrawal status
      await updateDoc(doc(db, 'withdrawals', w.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
      });

      // Wallet history — refund
      await addDoc(collection(db, 'walletHistory'), {
        userId: w.userId,
        type: 'refund',
        amount: w.amount,
        note: `Withdrawal rejected — ৳${w.amount} refunded`,
        balanceAfter: newBalance,
        createdAt: serverTimestamp(),
      });

      alert('❌ Withdrawal rejected — টাকা ফেরত দেওয়া হয়েছে।');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(null);
  };

  const filtered = withdrawals.filter(w => {
    if (filter === 'all') return true;
    return w.status === filter;
  });

  const totalPending = withdrawals
    .filter(w => w.status === 'pending')
    .reduce((s, w) => s + (w.amount ?? 0), 0);

  const totalCompleted = withdrawals
    .filter(w => w.status === 'completed')
    .reduce((s, w) => s + (w.amount ?? 0), 0);

  const sidebarLinks = [
    { to: '/', label: '🏠 Dashboard' },
    { to: '/matches', label: '🎯 Matches' },
    { to: '/results', label: '🏆 Results' },
    { to: '/users', label: '👥 Users' },
    { to: '/deposits', label: '💰 Deposits' },
    { to: '/withdrawals', label: '⬆️ Withdrawals', active: true },
    { to: '/settings', label: '⚙️ Settings' },
    { to: '/password-resets', label: '🔑 Password Resets' },
    { to: '/videos', label: '🎬 Videos' }
  ];

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.pageTitle}>⬆️ Withdrawals</h1>
            <p style={styles.pageSub}>User দের withdrawal request manage করো</p>
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={{ color: '#f4c430', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              {withdrawals.filter(w => w.status === 'pending').length}
            </div>
            <div style={styles.statLabel}>Pending Requests</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#ff6b35', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              ৳{totalPending}
            </div>
            <div style={styles.statLabel}>Pending Amount</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#00c896', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              ৳{totalCompleted}
            </div>
            <div style={styles.statLabel}>Total Completed</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div style={styles.filterRow}>
          {[
            { key: 'pending', label: '⏳ Pending' },
            { key: 'completed', label: '✅ Completed' },
            { key: 'rejected', label: '❌ Rejected' },
            { key: 'all', label: '📋 All' },
          ].map(f => (
            <button key={f.key}
              style={filter === f.key ? styles.filterActive : styles.filterBtn}
              onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['User ID', 'Method', 'Number', 'Amount', 'Balance After', 'Time', 'Status', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#555', padding: 40 }}>
                    কোনো withdrawal নেই
                  </td>
                </tr>
              ) : filtered.map(w => (
                <tr key={w.id}>
                  <td style={styles.td}>
                    <span style={{ color: '#888', fontSize: 11 }}>{w.userId?.slice(0, 10)}...</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      background: w.method === 'bKash' ? '#e2136e22' : '#f4732a22',
                      color: w.method === 'bKash' ? '#e2136e' : '#f4732a',
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold',
                    }}>{w.method}</span>
                  </td>
                  <td style={styles.td}>{w.number}</td>
                  <td style={styles.td}>
                    <span style={{ color: '#ff6b35', fontWeight: 'bold', fontSize: 15 }}>
                      ৳{w.amount}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: '#888' }}>৳{w.balanceAfter ?? '—'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: '#666', fontSize: 12 }}>
                      {w.createdAt?.seconds
                        ? new Date(w.createdAt.seconds * 1000).toLocaleString()
                        : '—'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      background: w.status === 'pending' ? '#f4c43022'
                        : w.status === 'completed' ? '#00c89622' : '#ff444422',
                      color: w.status === 'pending' ? '#f4c430'
                        : w.status === 'completed' ? '#00c896' : '#ff4444',
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold',
                    }}>
                      {w.status === 'pending' ? '⏳ Pending'
                        : w.status === 'completed' ? '✅ Completed' : '❌ Rejected'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {w.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          style={{ ...styles.completeBtn, opacity: loading === w.id ? 0.6 : 1 }}
                          onClick={() => handleComplete(w)}
                          disabled={loading === w.id}
                        >
                          {loading === w.id ? '...' : '✅ Complete'}
                        </button>
                        <button
                          style={{ ...styles.rejectBtn, opacity: loading === w.id ? 0.6 : 1 }}
                          onClick={() => handleReject(w)}
                          disabled={loading === w.id}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: '#555', fontSize: 12 }}>
                        {w.status === 'completed'
                          ? new Date((w.completedAt?.seconds ?? 0) * 1000).toLocaleString()
                          : 'Rejected'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub: { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#888', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent' },
  navItemActive: { color: '#f4c430', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430', background: '#f4c43011' },
  main: { marginLeft: 240, flex: 1, padding: 32 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub: { color: '#666', fontSize: 14, marginTop: 6 },
  statsRow: { display: 'flex', gap: 16, marginBottom: 24 },
  statCard: {
    background: '#111128', borderRadius: 14, border: '1px solid #1e1e3a',
    padding: '20px 28px', minWidth: 180,
  },
  statLabel: { color: '#666', fontSize: 13 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 20 },
  filterBtn: {
    background: '#111128', border: '1px solid #333', borderRadius: 10,
    color: '#888', fontSize: 13, fontWeight: '600', padding: '8px 18px', cursor: 'pointer',
  },
  filterActive: {
    background: '#f4c430', border: '1px solid #f4c430', borderRadius: 10,
    color: '#000', fontSize: 13, fontWeight: '600', padding: '8px 18px', cursor: 'pointer',
  },
  tableCard: { background: '#111128', borderRadius: 16, border: '1px solid #1e1e3a', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#666', fontSize: 12, fontWeight: '600', padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #1e1e3a', whiteSpace: 'nowrap' },
  td: { color: '#ccc', fontSize: 13, padding: '14px 16px', borderBottom: '1px solid #1e1e3a11' },
  completeBtn: {
    background: '#00c89622', border: '1px solid #00c89644',
    color: '#00c896', borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600', whiteSpace: 'nowrap',
  },
  rejectBtn: {
    background: '#ff444422', border: '1px solid #ff444444',
    color: '#ff6b6b', borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600',
  },
};